/**
 * GET /api/v1/print-options and GET /api/v1/serviceability.
 *
 * Both are read-only catalogue lookups, both are safe to cache, and neither
 * needs a session — the options screen has to work before the customer signs in.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type { PrintOptionOut, ServiceabilityOut } from './types';

export const PRINT_OPTIONS_PATH = '/api/v1/print-options';
export const SERVICEABILITY_PATH = '/api/v1/serviceability';

export function fetchPrintOptions(signal?: AbortSignal): Promise<PrintOptionOut[]> {
  return api.get<PrintOptionOut[]>(PRINT_OPTIONS_PATH, signal ? { signal } : undefined);
}

/**
 * The preset catalogue. Cached for five minutes: an operator changing a price
 * mid-session must reach a long-lived tab eventually, but re-fetching the same
 * four rows on every step change is pure waste on a metered connection.
 */
export function usePrintOptions() {
  return useApiQuery<PrintOptionOut[], typeof queryKeys.printOptions>({
    queryKey: queryKeys.printOptions,
    queryFn: ({ signal }) => fetchPrintOptions(signal),
    staleTime: 5 * 60_000,
  });
}

export function fetchServiceability(
  pincode: string,
  signal?: AbortSignal,
): Promise<ServiceabilityOut> {
  return api.get<ServiceabilityOut>(SERVICEABILITY_PATH, {
    query: { pincode },
    ...(signal ? { signal } : {}),
  });
}

/**
 * "Do we deliver here?" for one pincode.
 *
 * Only enabled once the pincode is six digits: firing on every keystroke would
 * send five guaranteed-422 requests per address, and the server's own regex
 * rejects anything shorter anyway. A serviceable/not answer for a given pincode
 * does not change minute to minute, so it is cached for the session.
 */
export function useServiceability(pincode: string | null) {
  const ready = typeof pincode === 'string' && /^[1-9][0-9]{5}$/.test(pincode);
  return useApiQuery<ServiceabilityOut, ReturnType<typeof queryKeys.serviceability>>({
    queryKey: queryKeys.serviceability(pincode ?? ''),
    queryFn: ({ signal }) => fetchServiceability(pincode ?? '', signal),
    enabled: ready,
    staleTime: 10 * 60_000,
  });
}
